import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { database } from './firebase';
import { ref, onValue, set, serverTimestamp } from 'firebase/database';

function TabletWaiting() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [tableInfo, setTableInfo] = useState(null);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Responsive helper - visual only
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    // Verify tablet is logged in
    const storedTableId = localStorage.getItem('tabletTableId');
    if (!storedTableId || storedTableId !== tableId) {
      navigate('/tablet-login');
      return;
    }

    // CRITICAL: Reset status to "online" when returning to waiting screen
    const resetToOnline = async () => {
      const statusRef = ref(database, `tablets/${tableId}/status`);
      const pingRef = ref(database, `tablets/${tableId}/lastPing`);
      await set(statusRef, 'online');
      await set(pingRef, serverTimestamp());
      console.log('✅ Tablet status reset to online');
    };
    
    resetToOnline();

    // Listen to tablet status
    const tabletRef = ref(database, `tablets/${tableId}`);
    const unsubscribe = onValue(tabletRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setTableInfo(data);
        setCurrentMatch(data.currentMatch);
        setLoading(false);

        // If match assigned, navigate to match screen with display view
        if (data.currentMatch && data.currentMatch.sessionId) {
          navigate(`/match/${data.currentMatch.sessionId}?view=display&tablet=true&tableId=${tableId}`);
        }
      } else {
        // Tablet registration removed, go back to login
        handleLogout();
      }
    });

    // Start heartbeat
    const heartbeatInterval = setInterval(() => {
      const pingRef = ref(database, `tablets/${tableId}/lastPing`);
      set(pingRef, serverTimestamp());
    }, 30000);

    return () => {
      unsubscribe();
      clearInterval(heartbeatInterval);
    };
  }, [tableId, navigate]);

  // Screen Wake Lock - Keep screen on while waiting for match
  // Uses multiple methods for maximum compatibility
  useEffect(() => {
    let wakeLock = null;
    let noSleepVideo = null;
    let wakeLockMethod = 'none';
    
    const requestWakeLock = async () => {
      // Method 1: Screen Wake Lock API (iOS 16.4+, modern Android/Chrome)
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          // Verify it wasn't silently denied (iOS can do this)
          if (!wakeLock.released) {
            wakeLockMethod = 'api';
            console.log('🔆 Screen Wake Lock acquired via API');
            wakeLock.addEventListener('release', () => {
              console.log('🔅 Screen Wake Lock released');
              wakeLockMethod = 'none';
            });
            return;
          } else {
            console.log('⚠️ Wake Lock API returned a released lock — falling back');
          }
        }
      } catch (err) {
        console.log('⚠️ Wake Lock API failed:', err.message, '— falling back');
      }
      
      // Method 2: Non-muted video at near-zero volume.
      // iOS Safari ignores muted video for screen wake purposes, but a non-muted
      // video playing at minimal volume (0.01) keeps the screen awake on both
      // iOS Safari and Android Chrome. This is the same approach used by NoSleep.js.
      try {
        if (!noSleepVideo) {
          noSleepVideo = document.createElement('video');
          noSleepVideo.setAttribute('playsinline', '');
          // NOT muted — iOS only keeps screen awake for non-muted media
          noSleepVideo.setAttribute('loop', '');
          noSleepVideo.volume = 0.01; // Near-zero volume — inaudible but iOS considers it "audible media"
          noSleepVideo.style.position = 'absolute';
          noSleepVideo.style.left = '-9999px';
          noSleepVideo.style.width = '1px';
          noSleepVideo.style.height = '1px';
          noSleepVideo.src = 'data:video/webm;base64,GkXfowEAAAAAAAAfQoaBAUL3gQFC8oEEQvOBCEKChHdlYm1Ch4EEQoWBAhhTgGcBAAAAAAAH4xFNm3RALE27i1OrhBVJqWZTrIHfTbuMU6uEFlSua1OsggEuTbuMU6uEHFO7a1OsggI+7AEAAAAAAACkAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVSalmAQAAAAAAAEMq17GDD0JATYCNTGF2ZjU4LjI5LjEwMFdBjUxhdmY1OC4yOS4xMDBzpJBhAAAAAAAWVK5rAQAAAAAAADuuAQAAAAAAADLXgQFzxYEBnIEAIrWcg3VuZIaFVl9WUDmDgQEj44OEAmJhAAAAAABiZIEgY6KTT2QAAAAAAAAAM8eBAUSHgQNH';
          document.body.appendChild(noSleepVideo);
          
          const playVideo = () => {
            noSleepVideo.play().then(() => {
              wakeLockMethod = 'video';
              console.log('🔆 NoSleep video playing (non-muted, vol 0.01) — screen will stay on');
            }).catch(e => {
              console.log('⚠️ NoSleep video play failed:', e.message);
            });
          };
          // Try immediately and also on next user gesture (iOS may need a gesture for non-muted video)
          playVideo();
          document.addEventListener('click', playVideo, { once: true });
          document.addEventListener('touchstart', playVideo, { once: true });
        }
      } catch (err) {
        console.log('⚠️ NoSleep fallback failed:', err.message);
      }
    };
    
    requestWakeLock();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
      if (noSleepVideo) {
        noSleepVideo.pause();
        noSleepVideo.remove();
      }
    };
  }, []);

  const handleLogout = () => {
    // Remove from Firebase
    const tabletRef = ref(database, `tablets/${tableId}`);
    set(tabletRef, null);

    // Clear localStorage
    localStorage.removeItem('tabletTableId');
    const heartbeatInterval = localStorage.getItem('heartbeatInterval');
    if (heartbeatInterval) {
      clearInterval(parseInt(heartbeatInterval));
      localStorage.removeItem('heartbeatInterval');
    }

    // Navigate to login
    navigate('/tablet-login');
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#0d1321',
      color: '#f0f2f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'DM Sans', sans-serif",
      backgroundImage: 'radial-gradient(ellipse at 50% 50%, rgba(16, 185, 129, 0.04) 0%, transparent 60%)'
    },
    card: {
      backgroundColor: '#151c2e',
      borderRadius: '14px',
      padding: isMobile ? '24px' : '48px',
      maxWidth: '600px',
      width: '100%',
      textAlign: 'center',
      border: '1px solid #2a3555',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
    },
    icon: {
      fontSize: isMobile ? '48px' : '72px',
      marginBottom: '24px',
    },
    logo: {
      width: isMobile ? '64px' : '80px',
      height: isMobile ? '64px' : '80px',
      borderRadius: '14px',
      marginBottom: '24px',
    },
    title: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '700',
      marginBottom: '8px',
      fontFamily: "'Outfit', sans-serif",
      letterSpacing: '-0.01em'
    },
    subtitle: {
      fontSize: '16px',
      color: '#8b95a8',
      marginBottom: '32px',
    },
    info: {
      backgroundColor: '#1c2640',
      borderRadius: '10px',
      padding: '16px',
      marginBottom: '16px',
      border: '1px solid #2a3555'
    },
    infoLabel: {
      fontSize: '12px',
      color: '#5a6478',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: '500'
    },
    infoValue: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#f0f2f5',
    },
    pulse: {
      display: 'inline-block',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: '#10b981',
      marginRight: '8px',
      animation: 'pulse 2s infinite',
      boxShadow: '0 0 8px rgba(16, 185, 129, 0.4)'
    },
    status: {
      fontSize: '15px',
      color: '#10b981',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '500',
      marginTop: '8px'
    },
    logoutButton: {
      padding: '10px 24px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      fontSize: '14px',
      fontFamily: "'DM Sans', sans-serif",
      cursor: 'pointer',
      marginTop: '16px',
      transition: 'all 0.2s ease'
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>⏳</div>
          <h1 style={styles.title}>Loading...</h1>
        </div>
      </div>
    );
  }

  if (!tableInfo) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>❌</div>
          <h1 style={styles.title}>Table Not Found</h1>
          <p style={styles.subtitle}>Please login again</p>
          <button style={styles.logoutButton} onClick={handleLogout}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
      <div style={styles.card}>
        <img src="/icon.png" alt="Cue Clock Pro" style={styles.logo} />
        <h1 style={styles.title}>Table {tableInfo.tableNumber} Ready</h1>
        <p style={styles.subtitle}>Waiting for match assignment...</p>

        <div style={styles.info}>
          <div style={styles.infoLabel}>Venue</div>
          <div style={styles.infoValue}>{tableInfo.venueName}</div>
        </div>

        <div style={styles.info}>
          <div style={styles.infoLabel}>Table Code</div>
          <div style={styles.infoValue}>{tableInfo.scoreboardCode}</div>
        </div>

        <div style={styles.status}>
          <span style={styles.pulse}></span>
          Online and Ready
        </div>

        <p style={{fontSize: '13px', color: '#5a6478', marginTop: '32px'}}>
          The organiser will assign a match to this table when ready.
          Keep this screen open.
        </p>

        <button style={styles.logoutButton} onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
}

export default TabletWaiting;